/* eslint-disable camelcase */
import React from 'react';
import AnimationWrapper from '../../../../components/animation/animationWrapper';
import Button from '../../../../components/button';
import { SkeletonCircle, SkeletonLine } from '../../../../components/skeleton';
import Heading from '../../../../components/typography';
import { useBoolean } from '../../../../hooks';
import { getProfileListData, type ProfileComponentType } from '../company';
import { ProfileContainer, ProfileContentWrapper } from '../profile.styled';
import HeadquarterModal from './headquarterModal';
import { type Wealthlane_CustomerService_Dtos_VendorCompany_CompanyAddressDto } from '@wealthlane/api-services/wealthlane-customer-service';
import AdditionalAddressModal from './additionalAddressModal';
import { taxFormatWithDash } from '../../../../utils/global';

const HeadquarterComponent = ({ data, loading }: ProfileComponentType): JSX.Element => {
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isAddressModalOpen,
    setFalse: closeAddressModal,
    setTrue: openAddressModal,
  } = useBoolean(false);

  const HeadquarterInfo = [
    {
      title: 'Address Line 1',
      icon: 'location_on',
      isContent: true,
      content: getProfileListData(data?.addressLine1),
    },
    {
      title: 'Address Line 2 (Optional)',
      icon: 'add_location_alt',
      isContent: true,
      content: getProfileListData(data?.addressLine2),
    },
    {
      title: 'Country',
      icon: 'flag',
      isContent: true,
      content: getProfileListData(data?.country),
    },
    {
      title: 'City',
      icon: 'location_city',
      isContent: true,
      content: getProfileListData(data?.city),
    },
    {
      title: 'State',
      icon: 'map',
      isContent: true,
      content: getProfileListData(data?.state),
    },
    {
      title: 'ZIP Code',
      icon: 'pin',
      isContent: true,
      content: getProfileListData(data?.zipCode),
    },
  ];

  const GetAdditionalInfo = ({
    details,
  }: {
    details: Wealthlane_CustomerService_Dtos_VendorCompany_CompanyAddressDto;
  }): JSX.Element => {
    const AdditionalInfo = [
      {
        title: 'Address Line 1',
        icon: 'location_on',
        isContent: true,
        content: getProfileListData(details?.addressLine1),
      },
      {
        title: 'Address Line 2 (Optional)',
        icon: 'add_location_alt',
        isContent: true,
        content: getProfileListData(details?.addressLine2),
      },
      {
        title: 'Tax ID',
        icon: 'mail',
        isContent: true,
        content: getProfileListData(
          (details?.taxId as unknown as boolean) ? taxFormatWithDash(String(details?.taxId)) : ''
        ),
      },
      {
        title: 'Country',
        icon: 'flag',
        isContent: true,
        content: getProfileListData(details?.countryText),
      },
      {
        title: 'City',
        icon: 'location_city',
        isContent: true,
        content: getProfileListData(details?.cityText),
      },
      {
        title: 'State',
        icon: 'map',
        isContent: true,
        content: getProfileListData(details?.stateText),
      },
      {
        title: 'ZIP Code',
        icon: 'pin',
        isContent: true,
        content: getProfileListData(details?.zipCode),
      },
    ];

    return (
      <div className='mb-5'>
        {AdditionalInfo.map((item) => (
          <div className='list grid sm:grid-cols-4 lg:grid-cols-12'>
            <div className='list-icon sm:col-span-2 lg:col-span-4'>
              <span className='material-symbols-outlined'>{item.icon}</span>
              <Heading title={item.title} variant='body-s' />
            </div>
            <div className='list-content sm:col-span-2 lg:col-span-8'>{item.content}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div id="headquarterComponentContainer">
      <ProfileContentWrapper className='mb-10' id="headquarterAddressWrapper">
        <div className='profile-header' id="headquarterAddressHeader">
          <Heading title='Headquarter Address Details' variant='h5' id="headquarterAddressTitle" />
          <Button
            as='Edit'
            variant={'secondary-dark'}
            title='Edit'
            icon='edit'
            id="headquarterAddressEditButton"
            onClick={() => {
              openModal();
            }}
          />
        </div>
        <ProfileContainer id="headquarterAddressContainer">
          {loading && (
            <div id="headquarterAddressLoadingContainer">
              {HeadquarterInfo.map((item, index) => (
                <div className='list list-loading' key={index} id={`headquarterAddressLoadingItem.${index}`}>
                  <div className='list-icon' id={`headquarterAddressLoadingIcon.${index}`}>
                    <SkeletonCircle size='table' shape='circle' />
                  </div>
                  <div className='list-content grid sm:grid-cols-4 lg:grid-cols-12' id={`headquarterAddressLoadingContent.${index}`}>
                    <SkeletonLine size='table' containerClassName='sm:col-span-2 lg:col-span-3' />
                    <SkeletonLine size='table' containerClassName='sm:col-span-2 lg:col-span-9' />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && (
            <div id="headquarterAddressDataContainer">
              {HeadquarterInfo.map((item, index) => (
                <div className='list grid sm:grid-cols-4 lg:grid-cols-12' key={index} id={`headquarterAddressItem.${index}`}>
                  <div className='list-icon sm:col-span-2 lg:col-span-4' id={`headquarterAddressItemIcon.${index}`}>
                    <span className='material-symbols-outlined' id={`headquarterAddressItemIconSpan.${index}`}>{item.icon}</span>
                    <Heading title={item.title} variant='body-s' id={`headquarterAddressItemTitle.${index}`} />
                  </div>
                  <div className='list-content sm:col-span-2 lg:col-span-8' id={`headquarterAddressItemContent.${index}`}>{item.content}</div>
                </div>
              ))}
            </div>
          )}
        </ProfileContainer>
      </ProfileContentWrapper>
      <ProfileContentWrapper id="additionalAddressWrapper">
        <div className='profile-header' id="additionalAddressHeader">
          <Heading title='Additional Address Details' variant='h5' id="additionalAddressTitle" />
          <Button
            as='Edit'
            variant={'secondary-dark'}
            title='Edit'
            icon='edit'
            id="additionalAddressEditButton"
            onClick={() => {
              openAddressModal();
            }}
          />
        </div>
        {loading && (
          <ProfileContainer id="additionalAddressLoadingContainer">
            {HeadquarterInfo.map((item, index) => (
              <div className='list list-loading' key={index} id={`additionalAddressLoadingItem.${index}`}>
                <div className='list-icon' id={`additionalAddressLoadingIcon.${index}`}>
                  <SkeletonCircle size='table' shape='circle' />
                </div>
                <div className='list-content grid sm:grid-cols-4 lg:grid-cols-12' id={`additionalAddressLoadingContent.${index}`}>
                  <SkeletonLine size='table' containerClassName='sm:col-span-2 lg:col-span-3' />
                  <SkeletonLine size='table' containerClassName='sm:col-span-2 lg:col-span-9' />
                </div>
              </div>
            ))}
          </ProfileContainer>
        )}
        {!loading && (
          <div id="additionalAddressDataContainer">
            {data?.companyAddressList?.map(
              (item: Wealthlane_CustomerService_Dtos_VendorCompany_CompanyAddressDto, index: number) => (
                <ProfileContainer key={index} id={`additionalAddressItemContainer.${index}`}>
                  <GetAdditionalInfo details={item} />
                </ProfileContainer>
              )
            )}
          </div>
        )}
      </ProfileContentWrapper>
      <AnimationWrapper id="headquarterModalAnimationWrapper">
        {isModalOpen && (
          <HeadquarterModal
            initialData={data}
            loading={loading}
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            id="headquarterModal"
          />
        )}
      </AnimationWrapper>
      <AnimationWrapper id="additionalAddressModalAnimationWrapper">
        {isAddressModalOpen && (
          <AdditionalAddressModal
            initialData={data}
            loading={loading}
            isModalOpen={isAddressModalOpen}
            closeModal={closeAddressModal}
            id="additionalAddressModal"
          />
        )}
      </AnimationWrapper>
    </div>
  );
};

export default HeadquarterComponent;
